import React from 'react';
import {
  filter,
  map,
  pipe,
  uniq,
  sortBy,
  identity
} from 'ramda';
import {
  DufBulkImportGeometryEntityDto,
  DufBulkImportParseResponse,
  DufLayerRefDto,
  FileParameter,
  useParseDufForBulkImport,
  useUpdateStopeGeometryFile
} from 'providers/api';
import { i18n } from 'i18n';
import { Model } from '../hooks/useModelState';
import { ImportedSolid } from '../MineModel/ImportedSolids/ImportedSolid';

interface UseDufStopeGeometryImportProps {
  entityId: string;
  stopeId: string;
  model: Model;
  onImportSuccess?: () => void;
}

const toImportedSolid = (entity: DufBulkImportGeometryEntityDto): ImportedSolid | null => {
  if (!entity.guid || !entity.objData) return null;
  const bb = entity.boundingBox;
  return {
    guid: entity.guid,
    objData: entity.objData,
    name: entity.layerName || entity.type,
    boundingBox: bb ? {
      minX: bb.minX ?? 0,
      maxX: bb.maxX ?? 0,
      minY: bb.minY ?? 0,
      maxY: bb.maxY ?? 0,
      minZ: bb.minZ ?? 0,
      maxZ: bb.maxZ ?? 0,
      centerX: bb.centerX ?? (((bb.minX ?? 0) + (bb.maxX ?? 0)) / 2),
      centerY: bb.centerY ?? (((bb.minY ?? 0) + (bb.maxY ?? 0)) / 2),
      centerZ: bb.centerZ ?? (((bb.minZ ?? 0) + (bb.maxZ ?? 0)) / 2),
      volume: bb.volume,
    } : undefined,
  };
};

const noSupportedGeometryMessage = () => i18n._(
  'No supported stope geometry was found in this DUF. Reticulation exports do not contain stope solids; upload a DUF with mesh entities instead.',
);

const useDufStopeGeometryImport = ({ entityId, stopeId, model, onImportSuccess }: UseDufStopeGeometryImportProps) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [isParsing, setIsParsing] = React.useState(false);
  const [isImporting, setIsImporting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isImperial, setIsImperial] = React.useState(false);
  const [parseResponse, setParseResponse] = React.useState<DufBulkImportParseResponse | null>(null);
  const [selectedLayerGuid, setSelectedLayerGuid] = React.useState<string>('');
  const [selectedAttribute, setSelectedAttribute] = React.useState<string>('');
  const [selectedValue, setSelectedValue] = React.useState<string>('');
  const dufFileRef = React.useRef<File | null>(null);

  const parseMutation = useParseDufForBulkImport(entityId);
  const uploadMutation = useUpdateStopeGeometryFile(entityId);

  const modelRef = React.useRef(model);
  modelRef.current = model;

  const layersWithGeometry = React.useMemo<DufLayerRefDto[]>(() => {
    if (!parseResponse) return [];
    const geometryLayerGuids = new Set(
      (parseResponse.geometryEntities ?? [])
        .filter((e) => e.layerGuid)
        .map((e) => e.layerGuid as string),
    );
    return (parseResponse.layers ?? []).filter((layer) => layer.guid && geometryLayerGuids.has(layer.guid));
  }, [parseResponse]);

  const entitiesOnSelectedLayer = React.useMemo<DufBulkImportGeometryEntityDto[]>(() => {
    if (!parseResponse || !selectedLayerGuid) return [];
    return (parseResponse.geometryEntities ?? []).filter((e) => e.layerGuid === selectedLayerGuid);
  }, [parseResponse, selectedLayerGuid]);

  const availableAttributes = React.useMemo<string[]>(() => {
    if (entitiesOnSelectedLayer.length === 0) return [];
    const allKeys = entitiesOnSelectedLayer.flatMap((e) => Object.keys(e.attributes ?? {}));
    return pipe(
      uniq,
      sortBy(identity),
    )(allKeys) as string[];
  }, [entitiesOnSelectedLayer]);

  const uniqueValues = React.useMemo<string[]>(() => {
    if (!selectedAttribute || entitiesOnSelectedLayer.length === 0) return [];
    const values = pipe(
      filter((e: DufBulkImportGeometryEntityDto) => (e.attributes ?? {})[selectedAttribute] !== undefined),
      map((e: DufBulkImportGeometryEntityDto) => (e.attributes ?? {})[selectedAttribute]),
      uniq,
      sortBy(identity),
    )(entitiesOnSelectedLayer) as string[];
    return values;
  }, [selectedAttribute, entitiesOnSelectedLayer]);

  const matchingEntities = React.useMemo<DufBulkImportGeometryEntityDto[]>(() => {
    if (!selectedLayerGuid) return [];

    if (selectedValue && selectedAttribute) {
      return entitiesOnSelectedLayer.filter(
        (e) => (e.attributes ?? {})[selectedAttribute] === selectedValue,
      );
    }

    if (availableAttributes.length === 0 || entitiesOnSelectedLayer.length === 1) {
      return entitiesOnSelectedLayer;
    }

    return [];
  }, [selectedLayerGuid, selectedValue, selectedAttribute, entitiesOnSelectedLayer, availableAttributes.length]);

  // Push matching entities into the model's importedSolids bag so ModelRenderer
  // renders them as a live 3D preview beside the dialog. Cleared on reset.
  React.useEffect(() => {
    const m = modelRef.current;
    m.clearImportedSolids();
    matchingEntities.forEach((entity) => {
      const solid = toImportedSolid(entity);
      if (solid) m.addImportedSolid(solid);
    });
  }, [matchingEntities]);

  const resetState = React.useCallback(() => {
    setParseResponse(null);
    setSelectedLayerGuid('');
    setSelectedAttribute('');
    setSelectedValue('');
    setError(null);
    setIsImperial(false);
    dufFileRef.current = null;
    modelRef.current.clearImportedSolids();
  }, []);

  const handleDufFileSelected = React.useCallback((file: File) => {
    dufFileRef.current = file;
    modelRef.current.clearImportedSolids();
    setParseResponse(null);
    setError(null);
    setDialogOpen(true);
    setIsParsing(true);
    setIsImperial(false);
    setSelectedLayerGuid('');
    setSelectedAttribute('');
    setSelectedValue('');

    const fileParam: FileParameter = { data: file, fileName: file.name };

    parseMutation.mutate(
      { fileParam },
      {
        onSuccess: (response: DufBulkImportParseResponse) => {
          const geometryEntities = response.geometryEntities ?? [];
          const layerGuids = geometryEntities
            .map((entity) => entity.layerGuid)
            .filter((layerGuid): layerGuid is string => !!layerGuid);
          const uniqueLayerGuids = Array.from(new Set(layerGuids));

          setParseResponse(response);
          setIsImperial(response.imperialUnits ?? false);
          setSelectedLayerGuid(uniqueLayerGuids.length === 1 ? uniqueLayerGuids[0] : '');
          setDialogOpen(true);
          setError(geometryEntities.length === 0 ? noSupportedGeometryMessage() : null);
          setIsParsing(false);
        },
        onError: (err: any) => {
          const errorMessage = err?.result?.errors?.['']?.[0]
            || err?.result?.title
            || err?.message
            || 'Failed to parse DUF file';
          setError(errorMessage);
          setIsParsing(false);
        },
      },
    );
  }, [parseMutation]);

  const handleLayerChange = React.useCallback((layerGuid: string) => {
    setSelectedLayerGuid(layerGuid);
    setSelectedAttribute('');
    setSelectedValue('');
  }, []);

  const handleAttributeChange = React.useCallback((attribute: string) => {
    setSelectedAttribute(attribute);
    setSelectedValue('');
  }, []);

  const handleValueChange = React.useCallback((value: string) => {
    setSelectedValue(value);
  }, []);

  const handleImport = React.useCallback(() => {
    if (!dufFileRef.current || matchingEntities.length === 0) return;

    setIsImporting(true);
    setError(null);

    const firstEntity = matchingEntities[0];
    const file = dufFileRef.current;

    uploadMutation.mutate(
      {
        stopeId,
        file: { data: file, fileName: file.name },
        entityGuid: firstEntity.guid,
      },
      {
        onSuccess: () => {
          setIsImporting(false);
          setDialogOpen(false);
          resetState();
          onImportSuccess?.();
        },
        onError: (err: any) => {
          const errorMessage = err?.result?.errors?.['']?.[0]
            || err?.result?.title
            || err?.message
            || 'Failed to import geometry';
          setError(errorMessage);
          setIsImporting(false);
        },
      },
    );
  }, [matchingEntities, stopeId, uploadMutation, resetState]);

  const handleCancel = React.useCallback(() => {
    setDialogOpen(false);
    resetState();
  }, [resetState]);

  // Cleanup preview on unmount.
  React.useEffect(() => () => {
    modelRef.current.clearImportedSolids();
  }, []);

  return {
    dialogOpen,
    isParsing,
    isImporting,
    error,
    isImperial,
    layersWithGeometry,
    availableAttributes,
    uniqueValues,
    matchingEntities,
    selectedLayerGuid,
    selectedAttribute,
    selectedValue,
    handleDufFileSelected,
    handleLayerChange,
    handleAttributeChange,
    handleValueChange,
    handleImport,
    handleCancel,
  };
};

export default useDufStopeGeometryImport;
